import React from "react";
import axios from "axios";
import { Switch, Route, Redirect } from "react-router-dom";

import Loading from "../common/loading";
import parseQueryParam from "../../utils/parseQueryParams";
import AppLogout from "./autoLogout";

import Login from "./login";
import SamlLogin from "./samlLogin";
import Signup from "./signup";
import EmailVerification from "./emailVerification";
import PendingApproval from "./pendingApproval";
import ResetPassword from "./resetPassword";
import EnterpriseSetup from "./enterpriseSetup";
import ConfirmEmail from "./confirmEmail";

import NotificationManager from "../../utils/notificationManager";
import ModalManager from "../common/modal/ModalManager";
import {
  clearBrowserAuthSession,
  clearStoredAuthSession,
} from "../../utils/sessionLogout";
import {
  acknowledgeLoginNotice as storeLoginNoticeAcknowledgement,
  getLoginNoticeStorageKey,
  isLoginNoticeAcknowledged,
} from "../../utils/loginNotice";

// public pages
import BetaTerms from "./betaTerms";

/**
 *
 * CUSTOM STYLING
 *
 * Custom Domain: digiled.synaccess.com
 * -> redirects to cloud.synaccess.com/digiled
 * -> authwrapper: customDidMountFunction to set localstorage, and set styling, then redirect to login with new styling
 * -> login: customDidMountfunction to check localstorage, and change signups and stuff
 * ->
 *
 * cloud.synaccess.com
 * -> authwrapper: componentDidMount check localstorage for styling
 * -> on successful localstorage or query param login, apply enterprise styling returned by the backend
 * -> login: on submit, apply the authenticated user's enterprise styling
 */

const DEFAULT_LOGIN_TIME = 2100;
const CURRENT_FRONTEND_CHANNEL =
  process.env.REACT_APP_FRONTEND_CHANNEL || "stable";
const normalizePostLoginUrl = (url) => {
  const nextUrl = String(url || "").trim();
  return nextUrl.startsWith("/") && !nextUrl.startsWith("//") ? nextUrl : "/";
};

const DEFAULT_STYLING = {
  primary: "#141c25",
  primaryFont: "#ffffff",
  secondary: "#1d262f",
  secondaryFont: "#ffffff",
  tertiary: "#49535D",
  tertiaryFont: "#ffffff",
  quaternary: "#49535D",
  quaternaryFont: "#ffffff",
  quinary: "#6c757e",
  quinaryFont: "#000000",
  highlight: "#141c25",
  highlightFont: "#ffffff",
};

const STYLING_VARIABLES = {
  primary: "--primary-color",
  primaryFont: "--primary-font-color",
  secondary: "--secondary-color",
  secondaryFont: "--secondary-font-color",
  tertiary: "--tertiary-color",
  tertiaryFont: "--tertiary-font-color",
  quaternary: "--quaternary-color",
  quaternaryFont: "--quaternary-font-color",
  quinary: "--quinary-color",
  quinaryFont: "--quinary-font-color",
  highlight: "--highlight-color",
  highlightFont: "--highlight-font-color",
};

const hasStylingValues = (styling) =>
  styling &&
  typeof styling === "object" &&
  Object.keys(styling).some((key) => String(styling[key] || "").trim());

const loginNoticeBannerStyles = {
  wrapper: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
    padding: "14px 16px",
    borderRadius: 6,
    border: "1px solid #f59e0b",
    background: "#fffbeb",
    color: "#1f2937",
    boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
  },
  body: {
    flex: "1 1 auto",
    maxHeight: "180px",
    overflow: "auto",
    whiteSpace: "pre-wrap",
    fontSize: "13px",
    lineHeight: 1.55,
  },
  action: {
    flex: "0 0 auto",
    border: "1px solid #b45309",
    borderRadius: 5,
    background: "#ffffff",
    color: "#92400e",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    padding: "8px 12px",
  },
};

const LoginNoticeBanner = ({ notice, onAcknowledge }) => (
  <section style={loginNoticeBannerStyles.wrapper} aria-label="Login notice">
    <div style={loginNoticeBannerStyles.body}>{notice.text}</div>
    <button
      type="button"
      style={loginNoticeBannerStyles.action}
      onClick={onAcknowledge}
    >
      Acknowledge
    </button>
  </section>
);

function authWrapper(WrappedComponent) {
  return class extends React.Component {
    state = {
      authorized: false,
      userLoading: false,
      user: null,
      errMessage: "",
      signupErrMessage: "",
      loginNoticeAckedKey: "",
    };
    componentDidMount() {
      this.processLocalStorageOrQueryParameters();
    }

    processStyling = (styling = {}) => {
      console.log("Processing Styling");
      const resolvedStyling = {
        ...DEFAULT_STYLING,
        ...(styling || {}),
      };

      Object.entries(STYLING_VARIABLES).forEach(([key, variableName]) => {
        document.documentElement.style.setProperty(
          variableName,
          resolvedStyling[key] || DEFAULT_STYLING[key],
        );
      });
    };

    resetStyling = () => {
      this.processStyling(DEFAULT_STYLING);
    };

    syncEnterpriseStyling = (user) => {
      const styling = user?.enterprise?.ui_theme;

      if (user?.enterprise_Id && hasStylingValues(styling)) {
        localStorage.setItem("styling", JSON.stringify(styling));
        this.processStyling(styling);
        return;
      }

      localStorage.removeItem("styling");
      this.resetStyling();
    };

    /**
     * You can prelogin with 2 separate ways
     * 1. Query Parameters
     *  - t: token
     *  - u: userId
     *  - These are available through enterprise SSO redirects from IdP initiated login flows. They use the default login time.
     * 2. Local Storage
     *  - token
     *  - userId
     *  - tokenExpiration
     *
     * when successfully authenticating either, a token is stored with an expiration time.
     * Enterprise SSO sign-in must always start through /v1/auth/ssoLogin so the backend can create a fresh provider request.
     *
     *
     */
    processLocalStorageOrQueryParameters = () => {
      const { history } = this.props;
      const cookie = localStorage.getItem("token");
      const token = parseQueryParam(history.location.search).t;
      const userId = parseQueryParam(history.location.search).u;
      if (
        [
          "/register",
          "/reset",
          "/confirm-email",
          "/enterprise-setup",
          // "/mettel"
          "/saml-login",
          "/digiled",
        ].includes(history.location.pathname)
      ) {
        // ignore register for auth
        if (
          history.location.pathname === "/confirm-email" &&
          localStorage.getItem("userId")
        ) {
          this.fetchUser();
        }
      } else {
        if (token) {
          // assume if there is a token in the query parameters that expiration time is 30s
          // console.log("PROCESSING QUERY PARAMETER TOKEN FROM SSO")
          localStorage.setItem("token", token);
          localStorage.setItem("loginMethod", "sso");
          localStorage.removeItem("samlLoginUrl");
          localStorage.setItem(
            "tokenExpiration",
            new Date().getTime() + DEFAULT_LOGIN_TIME * 1000,
          );
          localStorage.setItem("userId", userId);
          this.fetchUser((err) => {
            if (err) {
              // USER IS TRYING TO LOGIN WITH A FAULTY SSO TOKEN IN THE QUERY PARAMETER
              // console.log("SSO token is not valid")
              this.props.history.replace(
                `/login?redirect=${this.props.history.location.pathname === "/logout" ? "/" : this.props.history.location.pathname}`,
              );
            } else {
              // USER IS LOGGING IN WITH A SSO TOKEN IN THE QUERY PARAMETER
              // console.log("SSO token is valid.")
              this.loginTimeoutModal(DEFAULT_LOGIN_TIME * 1000); // logged in successfully with SSO
            }
          });
        } else if (cookie) {
          this.fetchUser((err) => {
            if (err) {
              // USER IS TRYING TO LOGIN WITH A FAULTY LOCAL STORAGE COOKIE
              if (err?.response?.status === 401) {
                clearStoredAuthSession();

                this.setState({
                  authorized: false,
                  // user: null
                });
                // console.log("CLEARING LOCAL STORAGE")
              }
              this.props.history.replace(
                `/login?redirect=${this.props.history.location.pathname === "/logout" ? "/" : this.props.history.location.pathname}`,
              );
            } else {
              // USER IS LOGGING IN WITH A GOOD LOCAL STORAGE COOKIE
              const tokenExpiration = localStorage.getItem("tokenExpiration");
              if (tokenExpiration) {
                const timeUntilExpiration =
                  parseInt(tokenExpiration, 10) - new Date().getTime();
                // console.log("TIME UNTIL EXPIRATION", timeUntilExpiration)
                if (timeUntilExpiration > 0) {
                  this.loginTimeoutModal(timeUntilExpiration);
                } else {
                  // console.log("Expired token expiration time, no timeout modal created for later")
                }
              } else {
                // console.log("NO TOKEN EXPIRATION TIME FOUND")
              }
              // this.loginTimeoutModal()
            }
          });
        } else {
          // console.log("NO LOGIN INFORMATION FOUND")
          // console.log("redirecting because no cookie")

          const { pathname } = history.location;
          const redirect =
            pathname === "/login" || pathname === "/"
              ? ""
              : `?redirect=${pathname}`;
          this.props.history.replace(`/login${redirect}`);
        }
      }
    };

    startSamlLoginForCurrentUser = () => {
      const email = this.state.user?.email_id;
      clearStoredAuthSession();

      if (!email) {
        this.props.history.replace("/saml-login");
        return;
      }

      this.fetchSamlLogin(email, (err, ssoLoginUrlObj) => {
        const ssoLoginUrl =
          ssoLoginUrlObj?.data?.SSO_LOGIN_URL || ssoLoginUrlObj?.data?.SAML_LOGIN_URL;
        if (err || !ssoLoginUrl) {
          this.setState({ userLoading: false });
          this.props.history.replace("/saml-login");
          return;
        }

        window.location = ssoLoginUrl;
      });
    };

    loginTimeoutModal = (timeoutInMs = DEFAULT_LOGIN_TIME * 1000) => {
      this.timer = setTimeout(() => {
        ModalManager.confirm(
          "You have been signed out. Please sign in to access Synaccess Cloud.",
          "Session Expired",
          "Sign In",
          (status, close) => {
            if (status) {
              // clicked sign in
              const loginMethod = localStorage.getItem("loginMethod");

              if (this.state.user && ["sso", "saml"].includes(loginMethod)) {
                this.startSamlLoginForCurrentUser();
              } else {
                console.log(
                  "Timeout, and Modal selected. Reprocessing credentials to redirect appropriately",
                );
                this.processLocalStorageOrQueryParameters();
                // this.props.history.replace(`/login?redirect=${this.props.history.location.pathname === "/logout" ? "/" : this.props.history.location.pathname}`)
              }
              close();
            } else {
              // clicked cancel or outside
            }
          },
          null,
          true,
        );
      }, timeoutInMs);
    };
    updateUser = (obj) => {
      this.setState(obj);
    };

    updateUserFetch = (user, body, cb = () => {}) => {
      axios
        .put(`/v1/users/${user.id}`, body, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        })
        .then((res) => {
          cb(null, res.data);
          console.log(res.data.data);
          this.setState({
            // replace single item in array for resource being edited, search by ID match
            user: res.data.data,
          });
        })
        .catch((err) => {
          cb(err, null);
          console.log(err);
        });
    };

    fetchUser = (cb = () => {}) => {
      this.setState({ userLoading: true });
      axios
        .get(`/v1/users/${localStorage.getItem("userId")}`, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        })
        .then((res) => {
          this.syncEnterpriseStyling(res.data.data);
          this.setState({ userLoading: false });
          this.setState({
            // users: res.data.data
            authorized: true,
            // ...res.data
            user: res.data.data,
          });
          cb(null, res.data);
        })
        .catch((err) => {
          this.setState({ userLoading: false });
          cb(err);
          console.log(err);
        });
    };
    fetchSamlLogin = async (username, cb = () => {}, stayLoggedIn) => {
      this.setState({
        userLoading: true,
        errMessage: "",
      });
      try {
        const axiosResult = await axios.post(
          "/v1/auth/ssoLogin",
          {
            email: username.trim(),
          },
          {
            headers: {
              "Content-Type": "application/json",
              // include any other headers you need here
            },
          },
        );
        if (axiosResult.status === 200) {
          cb(null, axiosResult.data);
        } else {
          cb(axiosResult.error);
          NotificationManager.alert("Unauthorized");
        }
      } catch (error) {
        if (error?.response?.status === 400) {
          NotificationManager.alert("Invalid SSO Login");
          this.setState({
            errMessage: "Invalid SSO Login",
          });
        } else if (error?.response?.status === 401) {
          NotificationManager.alert("Unauthorized");
          this.setState({
            errMessage: "Unauthorized",
          });
        } else {
          NotificationManager.alert("Error Occurred");
          this.setState({
            errMessage: "Error Occurred",
          });
        }
        cb(error);
        this.setState({
          userLoading: false,
        });
      }
    };
    fetchLogin = (username, password, cb = () => {}, stayLoggedIn) => {
      this.setState({
        userLoading: true,
        errMessage: "",
      });
      axios
        .post("/v1/auth/login", {
          email: username.trim(),
          password,
          stayLoggedIn,
          frontendChannel: CURRENT_FRONTEND_CHANNEL,
        })
        .then((res) => {
          cb();
          this.setState({
            userLoading: false,
          });
          if (res.status === 200) {
            localStorage.setItem("token", res.data.token);
            localStorage.setItem(
              "tokenExpiration",
              new Date().getTime() + res.data.tokenExpirationTime * 1000,
            ); // set token expiration by taking the current time and adding the expiration time
            localStorage.setItem("userId", res.data.user.user_id);
            localStorage.setItem("loginMethod", "password");
            localStorage.removeItem("samlLoginUrl");

            // call loginTimeoutModal and calculate the time until the token expires and set that as the parameter
            this.loginTimeoutModal(res.data.tokenExpirationTime * 1000);
            this.syncEnterpriseStyling(res.data.user);

            this.setState({
              authorized: true,
              user: res.data.user,
            });

            const redirectUrl = parseQueryParam(
              this.props.history.location.search,
            ).redirect;
            const nextUrl = normalizePostLoginUrl(redirectUrl);
            if (res.data.frontend_reload_required) {
              window.location.replace(nextUrl);
              return;
            }
            this.props.history.replace(nextUrl);
          } else {
            NotificationManager.alert("Incorrect email or password.");
            this.setState({
              errMessage: "Incorrect email or password.",
            });
          }
        })
        .catch((error) => {
          console.log(error);
          cb();
          NotificationManager.alert(
            `Username and/or Password Invalid. (${error.response.data.message})`,
          );
          this.setState({
            userLoading: false,
          });
          if (error.response.status === 401) {
            this.setState({
              // errMessage: "Username and/or Password Invalid."
              errMessage:
                error.response.data.message === "Unauthorized"
                  ? "Username and/or Password Invalid."
                  : error.response.data.message,
            });
          } else if (error.response.status === 400) {
            this.setState({
              errMessage: error.response.data.message,
            });
          } else {
            this.setState({
              errMessage: "Error Occurred",
            });
          }
        });
    };
    fetchSignup = ({ fullName, password, email, company }, cb = () => {}) => {
      this.setState({
        signupErrMessage: "",
      });
      axios
        .post("/v1/auth/register", {
          fullName: fullName,
          password,
          email,
          company,
        })
        .then((res) => {
          localStorage.setItem("token", res.data.token);
          localStorage.setItem("userId", res.data.user.user_id);
          this.setState({
            authorized: true,
            user: res.data.user,
          });
          this.props.history.replace("/email-verification");
        })
        .catch((err) => {
          console.log(err);
          cb(err);
          if (err.response) {
            this.setState({
              signupErrMessage: err.response.data.message,
            });
          }
        });
    };
    signout = () => {
      clearBrowserAuthSession();
      this.props.history.replace("/login");
      this.setState({
        authorized: false,
        user: null,
        loginNoticeAckedKey: "",
      });
    };

    shouldShowLoginNotice = (user) => {
      const key = getLoginNoticeStorageKey(user);
      return Boolean(
        key &&
          key !== this.state.loginNoticeAckedKey &&
          !isLoginNoticeAcknowledged(user),
      );
    };

    acknowledgeLoginNotice = () => {
      const key = storeLoginNoticeAcknowledgement(this.state.user);
      if (!key) {
        return;
      }
      this.setState({ loginNoticeAckedKey: key });
    };

    resendVerificationEmail = (cb = () => {}) => {
      axios
        .post(
          "/v1/auth/resend-verification",
          {},
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          },
        )
        .then((res) => {
          cb(null, res);
          NotificationManager.success("Verification email sent!");
        })
        .catch((err) => {
          console.log(err);
          cb(err, null);
        });
    };
    submitForgottenPassEmail = (email, cb = () => {}) => {
      axios
        .post(
          "/v1/auth/reset",
          {
            email,
          },
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          },
        )
        .then((res) => {
          cb(null, res);
        })
        .catch((err) => {
          console.log(err);
          cb(err, null);
        });
    };

    confirmEmail = ({ token, cookie, password }, cb = () => {}) => {
      axios
        .post("/v1/auth/confirm-email", { token, cookie, password }, {})
        .then((res) => {
          if (res.status === 200) {
            localStorage.setItem("token", res.data.token);
            localStorage.setItem("userId", res.data.data.user_id);
            this.setState(
              {
                authorized: true,
                user: res.data.data,
              },
              () => {
                cb(null, res);
                this.props.history.replace("/");
              },
            );
          } else {
            cb({ err: "nah" }, null);
          }
        })
        .catch((err) => {
          cb(err, null);
        });
    };
    resetPassword = ({ password, currentPass }, cb = () => {}) => {
      // const token = localStorage.getItem("token") || parseQueryParam(this.props.history.location.search).t
      const token = localStorage.getItem("token");
      const email_token = parseQueryParam(this.props.history.location.search).t;
      axios
        .post(
          "/v1/auth/reset/token",
          {
            token,
            email_token,
            password,
            currentPass,
          },
          {},
        )
        .then((res) => {
          if (res.status === 200) {
            cb(null, res.data);
            this.setState({
              user: res.data.data,
              authorized: true,
            });
            localStorage.setItem("token", res.data.token);
            localStorage.setItem("userId", res.data.data.user_id);
          } else {
            cb(res.data, null);
            console.log("bad response from reset token request");
          }
        })
        .catch((err) => {
          cb(err.response.data, null);
          console.log(err);
        });
    };
    setupEnterpriseUser = ({ password, currentPass }, cb = () => {}) => {
      // const token = localStorage.getItem("token") || parseQueryParam(this.props.history.location.search).t
      const token = localStorage.getItem("token");
      const email_token = parseQueryParam(this.props.history.location.search).t;
      axios
        .post(
          "/v1/auth/reset/enterprise-token",
          {
            token,
            email_token,
            password,
            currentPass,
          },
          {},
        )
        .then((res) => {
          if (res.status === 200) {
            cb(null, res.data);
            this.setState({
              user: res.data.data,
              authorized: true,
            });
            localStorage.setItem("token", res.data.token);
            localStorage.setItem("userId", res.data.data.user_id);
          } else {
            cb(res.data, null);
            // console.log("bad response from reset token request")
          }
        })
        .catch((err) => {
          cb(err.response.data, null);
          console.log(err);
        });
    };
    render() {
      const { history } = this.props;

      const userId = localStorage.getItem("userId");
      return (
        <Switch>
          <Route
            path="/register"
            render={() => {
              return (
                <Signup
                  fetchSignup={this.fetchSignup}
                  signupErrMessage={this.state.signupErrMessage}
                />
              );
            }}
          />
          <Route
            path="/login"
            render={() => {
              // return null
              const { authorized } = this.state;
              if (authorized) {
                const redirectUrl = parseQueryParam(
                  history.location.search,
                ).redirect;
                return <Redirect to={redirectUrl || "/"} />;
              } else {
                return (
                  <Login
                    fetchLogin={this.fetchLogin}
                    errMessage={this.state.errMessage}
                    {...this.props}
                  />
                );
              }
            }}
          />
          <Route
            path="/saml-login"
            render={() => {
              return (
                <SamlLogin
                  fetchSamlLogin={this.fetchSamlLogin}
                  errMessage={this.state.errMessage}
                />
              );
            }}
          />
          <Route
            path="/email-verification"
            render={() => {
              const { authorized } = this.state;
              const user = this.state.user;
              if (user && user.email_verified && authorized) {
                return <Redirect to={"/"} />;
              }
              if (!userId) {
                return <Redirect to={"/login"} />;
              }
              if (this.state.user && this.state.user.emailVerified) {
                return <Redirect to="/" />;
              }
              return (
                <EmailVerification
                  userId={userId}
                  history={this.props.history}
                  signout={this.signout}
                  resendVerificationEmail={this.resendVerificationEmail}
                />
              );
            }}
          />
          <Route
            path="/pending-approval"
            render={() => {
              const { authorized } = this.state;
              const user = this.state.user;
              if (user && user.approved && authorized) {
                return <Redirect to={"/"} />;
              }
              return (
                <PendingApproval
                  userId={userId}
                  signout={this.signout}
                  user={this.state.user}
                />
              );
            }}
          />
          <Route
            path="/confirm-email"
            render={() => {
              if (this.state.userLoading) return <Loading fixed />;

              return (
                <ConfirmEmail
                  userLoading={this.state.userLoading}
                  authorized={this.state.authorized}
                  user={this.state.user}
                  confirmEmail={this.confirmEmail}
                  history={this.props.history}
                />
              );
            }}
          />
          <Route
            path="/reset"
            render={() => {
              // const { authorized } = this.state;
              // const user = this.state.user;
              // if (user && user.approved && authorized) {
              //   return <Redirect to={"/"} />
              // }
              return (
                <ResetPassword
                  submitForgottenPassEmail={this.submitForgottenPassEmail}
                  history={this.props.history}
                  resetPassword={this.resetPassword}
                  updateUser={this.updateUser}
                />
              );
            }}
          />
          <Route
            path="/enterprise-setup"
            render={() => {
              return (
                <EnterpriseSetup
                  submitForgottenPassEmail={this.submitForgottenPassEmail}
                  history={this.props.history}
                  setupEnterpriseUser={this.setupEnterpriseUser}
                  updateUser={this.updateUser}
                />
              );
            }}
          />
          <Route
            path="/beta-terms"
            render={() => {
              return <BetaTerms />;
            }}
          />
          <Route
            render={() => {
              const { authorized } = this.state;
              if (this.state.userLoading) {
                return (
                  <Loading
                    size="40px"
                    black
                    fixed
                    description="Logging user in..."
                  />
                );
              } else if (!authorized) {
                return null;
              } else if (authorized) {
                if (!this.state.user) return null;
                if (!this.state.user.email_verified) {
                  return <Redirect to={"/email-verification"} />;
                }
                if (!this.state.user.approved) {
                  return <Redirect to={"/pending-approval"} />;
                }
                const loginNoticeBanner = this.shouldShowLoginNotice(
                  this.state.user,
                ) ? (
                  <LoginNoticeBanner
                    notice={this.state.user.loginNotice}
                    onAcknowledge={this.acknowledgeLoginNotice}
                  />
                ) : null;
                return (
                  <AppLogout updateUser={this.updateUser} history={history}>
                    <WrappedComponent
                      user={{
                        ...this.state.user,
                      }}
                      updateUser={this.updateUser}
                      updateUserFetch={this.updateUserFetch}
                      resetPassword={this.resetPassword}
                      loginNoticeBanner={loginNoticeBanner}
                      {...this.props}
                    />
                  </AppLogout>
                );
              } else {
                return (
                  <Redirect
                    to={`/login?redirect=${history.location.pathname === "/logout" ? "/" : history.location.pathname}`}
                  />
                );
              }
            }}
          />
        </Switch>
      );
    }
  };
}

export default authWrapper;
